import "./DocumentMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

function DocumentMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    document_name: "",
    code: "",
    isactive: 1,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);

  // ─── GET /document/list ───────────────────────────────────
  const getDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/document/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      const data = res.data.data || res.data;

      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load documents:", err.response?.data);
      toastError("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);
  const filteredDocuments = documents.filter((doc) => {
    const search = searchTerm.trim().toLowerCase();

    return (
      doc.document_name?.toLowerCase().includes(search) ||
      doc.code?.toLowerCase().includes(search)
    );
  });
  // ─── GET /document/{id} ───────────────────────────────────
  const openEdit = async (doc) => {
    setFormError("");
    setIsEditMode(true);
    setSelectedDoc(doc);
    setFormLoading(true);
    setShowForm(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/document/${doc.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      const data = res.data.data || res.data;
      setFormData({
        document_name: data.document_name || "",
        code: data.code || "",
        isactive: data.isactive !== undefined ? data.isactive : 1,
      });
    } catch (err) {
      console.error("Failed to load document:", err.response?.data);
      const msg = "Failed to load document details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    setSelectedDoc(null);
    setFormData({
      document_name: "",
      code: "",
      isactive: 1,
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);

    setFormData({
      document_name: "",
      code: "",
      isactive: 1,
    });

    setFormError("");
    setSelectedDoc(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Document Name: alphabets and spaces only
    if (name === "document_name") {
      newValue = value.replace(/[^A-Za-z\s]/g, "");

      // Prevent multiple spaces
      newValue = newValue.replace(/\s{2,}/g, " ");

      // Prevent starting with space
      newValue = newValue.replace(/^\s+/, "");

      if (newValue.length > 100) return;
    }

    // Document Code: alphabets and numbers only
    if (name === "code") {
      newValue = value.replace(/[^A-Za-z0-9]/g, "");

      // Convert code to uppercase
      newValue = newValue.toUpperCase();

      if (newValue.length > 30) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };
  const validateDocumentForm = () => {
    const documentName = formData.document_name.trim();
    const code = formData.code.trim();

    // Document Name
    if (!documentName) {
      const msg = "Document name is required.";
      setFormError(msg);
      toastWarning(msg);
      return false;
    }

    if (!/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(documentName)) {
      const msg = "Document name can contain only alphabets and spaces.";
      setFormError(msg);
      toastWarning(msg);
      return false;
    }

    if (documentName.length < 2) {
      const msg = "Document name must contain at least 2 characters.";
      setFormError(msg);
      toastWarning(msg);
      return false;
    }

    // Document Code
    if (!code) {
      const msg = "Document code is required.";
      setFormError(msg);
      toastWarning(msg);
      return false;
    }

    if (!/^[A-Za-z0-9]+$/.test(code)) {
      const msg = "Document code can contain only letters and numbers.";
      setFormError(msg);
      toastWarning(msg);
      return false;
    }

    if (code.length < 2) {
      const msg = "Document code must contain at least 2 characters.";
      setFormError(msg);
      toastWarning(msg);
      return false;
    }

    return true;
  };
  // ─── POST /document/add ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    if (!validateDocumentForm()) {
      return;
    }

    setFormLoading(true);

    try {
      const authToken = useAuthStore.getState().token;

      await axios.post(
        `${process.env.REACT_APP_API_URL}/document/add`,
        {
          document_name: formData.document_name.trim(),
          code: formData.code.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      toastSuccess("Document created successfully.");
      closeForm();
      getDocuments();
    } catch (err) {
      console.error("Create error:", err.response?.data);

      const detail = err.response?.data?.detail;

      if (Array.isArray(detail)) {
        const msg = detail
          .map((d) => `${d.loc?.join(".")} — ${d.msg}`)
          .join("\n");

        setFormError(msg);
        toastError(msg);
      } else {
        const msg = detail || "Failed to create document.";
        setFormError(msg);
        toastError(msg);
      }
    } finally {
      setFormLoading(false);
    }
  };
  // ─── PUT /document/update/{id} ────────────────────────────
  const handleUpdate = async () => {
    setFormError("");

    if (!validateDocumentForm()) {
      return;
    }

    setFormLoading(true);

    try {
      const authToken = useAuthStore.getState().token;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/document/update/${selectedDoc.id}`,
        {
          document_name: formData.document_name.trim(),
          code: formData.code.trim(),
          isactive: formData.isactive,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      toastSuccess("Document updated successfully.");
      closeForm();
      getDocuments();
    } catch (err) {
      console.error("Update error:", err.response?.data);

      const detail = err.response?.data?.detail;

      if (Array.isArray(detail)) {
        const msg = detail
          .map((d) => `${d.loc?.join(".")} — ${d.msg}`)
          .join("\n");

        setFormError(msg);
        toastError(msg);
      } else {
        const msg = detail || "Failed to update document.";
        setFormError(msg);
        toastError(msg);
      }
    } finally {
      setFormLoading(false);
    }
  };

  // ─── DELETE /document/delete/{id} ────────────────────────

  const toggleDocumentStatus = async (doc) => {
    const newStatus = doc.isactive === 1 ? 0 : 1;

    try {
      const authToken = useAuthStore.getState().token;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/document/update/${doc.id}`,
        {
          document_name: doc.document_name,
          code: doc.code,
          isactive: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, isactive: newStatus } : d)),
      );

      toastSuccess(
        newStatus === 1
          ? "Document activated successfully."
          : "Document deactivated successfully.",
      );
    } catch (err) {
      console.error("Status update error:", err.response?.data);

      toastError(
        err.response?.data?.detail || "Failed to update document status.",
      );
    }
  };

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="add-user-page">
        {/* Header */}
        <div className="add-user-header">
          <div className="back-btn">
            <button className="back-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="back-icon" />
            </button>
          </div>
          <div className="header-title">
            <h2>{isEditMode ? "Edit Document" : "Add Document"}</h2>
          </div>
        </div>

        <div className="user-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              Loading...
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="form-grid">
            {/* Document Name */}
            <div className="form-group">
              <label>Document Name *</label>
              <input
                name="document_name"
                value={formData.document_name}
                onChange={handleChange}
                placeholder="Document Name"
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label>Document Code *</label>
              <input
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="Document Code"
                maxLength={30}
              />
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="form-group">
                <label>Status</label>
                <div className="status-toggle">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={formData.isactive === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isactive: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="slider"></span>
                  </label>
                  <span>{formData.isactive === 1 ? "Active" : "Inactive"}</span>
                </div>
              </div>
            )}
          </div>

          <div className="form-footer">
            <button className="btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="save-btn"
              onClick={isEditMode ? handleUpdate : handleSubmit}
              disabled={formLoading}
            >
              {formLoading ? "Saving..." : isEditMode ? "Update " : "Save "}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  DOCUMENT LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="user-list-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>Document Master</h2>
          <p>Manage document types for your Club OS</p>
        </div>
        <div className="page-actions">
          <div className="gm-search-box">
            <span className="gm-search-icon">🔍</span>

            <input
              type="text"
              placeholder="Search documents or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="btn-primary" onClick={openAdd}>
            <i className="ti ti-plus"></i>
            <span>Add Document</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="document-table-card">
        <div className="document-table-responsive">
          <table className="document-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Document Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="document-empty-row">
                    Loading documents...
                  </td>
                </tr>
              ) : filteredDocuments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="document-empty-row">
                    {searchTerm
                      ? `No documents match "${searchTerm}"`
                      : "No documents found."}
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((doc, index) => (
                  <tr key={doc.id}>
                    <td>{index + 1}</td>

                    <td>
                      <div
                        className="document-cell-text"
                        data-tooltip={doc.document_name}
                      >
                        {doc.document_name}
                      </div>
                    </td>

                    <td>
                      <div
                        className="document-cell-text document-code-text"
                        data-tooltip={doc.code}
                      >
                        {doc.code}
                      </div>
                    </td>

                    <td>
                      <label className="document-switch">
                        <input
                          type="checkbox"
                          checked={doc.isactive === 1}
                          onChange={() => toggleDocumentStatus(doc)}
                        />
                        <span className="document-slider"></span>
                      </label>
                    </td>

                    <td className="document-action-cell">
                      <button
                        className="document-edit-btn"
                        onClick={() => openEdit(doc)}
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DocumentMaster;
